import { GripVertical, Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DndContext, PointerSensor, TouchSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ImageUpload } from "@/components/admin/ImageUpload";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { API, errMsg } from "@/lib/api";

const EMPTY = { name: "", description: "", image: "", parent_id: "", order: 0, active: true };

function CatRow({ c, onToggle, onEdit, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: c.id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1, position: "relative", zIndex: isDragging ? 10 : undefined }}
      className="flex items-center gap-3 p-3 rounded-lg border bg-card"
      data-testid={`category-row-${c.slug}`}
    >
      <button type="button" className="cursor-grab touch-none text-muted-foreground hover:text-foreground shrink-0" {...attributes} {...listeners} aria-label="Drag to reorder" data-testid={`drag-category-${c.slug}`}>
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="flex-1 min-w-0">
        <p className={`font-medium ${!c.active ? "text-muted-foreground line-through" : ""}`}>{c.name}</p>
        {c.description && <p className="text-xs text-muted-foreground truncate">{c.description}</p>}
      </div>
      <div className="flex items-center gap-1">
        <Switch checked={!!c.active} onCheckedChange={() => onToggle(c)} data-testid={`category-active-${c.slug}`} aria-label="Active" />
        <Button variant="ghost" size="icon" onClick={() => onEdit(c)} aria-label="Edit" data-testid={`edit-category-${c.slug}`}><Pencil className="h-4 w-4" /></Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="icon" className="text-destructive" aria-label="Delete" data-testid={`delete-category-${c.slug}`}><Trash2 className="h-4 w-4" /></Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete "{c.name}"?</AlertDialogTitle>
              <AlertDialogDescription>Categories with products or subcategories cannot be deleted until those are moved.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => onRemove(c)} data-testid={`confirm-delete-category-${c.slug}`}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

export default function AdminCategories() {
  const [categories, setCategories] = useState(null);
  const [dialog, setDialog] = useState(null); // {id?} editing or {} for new
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => API.get("/admin/categories").then((r) => setCategories(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);

  const openNew = (parent_id = "") => {
    setForm({ ...EMPTY, parent_id });
    setDialog({});
  };
  const openEdit = (c) => {
    setForm({ name: c.name, description: c.description || "", image: c.image || "", parent_id: c.parent_id || "", order: c.order || 0, active: !!c.active });
    setDialog(c);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Category name is required");
    setSaving(true);
    const payload = { ...form, parent_id: form.parent_id || null, order: Number(form.order) || 0 };
    try {
      if (dialog?.id) await API.patch(`/admin/categories/${dialog.id}`, payload);
      else await API.post("/admin/categories", payload);
      toast.success("Category saved");
      setDialog(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    try {
      await API.delete(`/admin/categories/${c.id}`);
      toast.success("Category deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const toggleActive = async (c) => {
    try {
      await API.patch(`/admin/categories/${c.id}`, { name: c.name, description: c.description || "", image: c.image || "", parent_id: c.parent_id || null, order: c.order || 0, active: !c.active });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } })
  );

  const persistGroupOrder = async (group, activeId, overId) => {
    if (!overId || activeId === overId) return;
    const newIds = arrayMove(group.map((c) => c.id), group.findIndex((c) => c.id === activeId), group.findIndex((c) => c.id === overId));
    try {
      await Promise.all(newIds.map((id, i) => {
        const c = group.find((x) => x.id === id);
        return API.patch(`/admin/categories/${id}`, { name: c.name, description: c.description || "", image: c.image || "", parent_id: c.parent_id || null, order: i, active: !!c.active });
      }));
      toast.success("Order saved");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!categories) return <Skeleton className="h-96 rounded-xl" />;

  const parents = categories.filter((c) => !c.parent_id).sort((a, b) => a.order - b.order);

  return (
    <div data-testid="admin-categories-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Categories</h1>
          <p className="text-xs text-muted-foreground mt-1">Drag <GripVertical className="inline h-3 w-3 -mt-0.5" /> to reorder — this controls menus and the homepage occasions grid.</p>
        </div>
        <Button onClick={() => openNew()} className="rounded-full" data-testid="add-category-btn"><Plus className="mr-2 h-4 w-4" /> Add category</Button>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => persistGroupOrder(parents, e.active.id, e.over?.id)}>
        <SortableContext items={parents.map((p) => p.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-8">
            {parents.map((p) => {
              const children = categories.filter((c) => c.parent_id === p.id).sort((a, b) => a.order - b.order);
              return (
                <Card key={p.id} data-testid={`category-group-${p.slug}`}>
                  <CardContent className="p-5">
                    <CatRow c={p} onToggle={toggleActive} onEdit={openEdit} onRemove={remove} />
                    <div className="ml-6 mt-3 space-y-2">
                      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => persistGroupOrder(children, e.active.id, e.over?.id)}>
                        <SortableContext items={children.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                          {children.map((c) => <CatRow key={c.id} c={c} onToggle={toggleActive} onEdit={openEdit} onRemove={remove} />)}
                        </SortableContext>
                      </DndContext>
                      <button onClick={() => openNew(p.id)} className="text-sm text-primary hover:underline flex items-center gap-1 mt-2" data-testid={`add-subcategory-${p.slug}`}>
                        <Plus className="h-3.5 w-3.5" /> Add subcategory
                      </button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle>{dialog?.id ? "Edit category" : "New category"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="cf-name">Name *</Label>
              <Input id="cf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="category-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cf-desc">Description</Label>
              <Textarea id="cf-desc" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="category-description-input" />
            </div>
            <div className="space-y-2">
              <Label>Parent category</Label>
              <Select value={form.parent_id || "none"} onValueChange={(v) => setForm({ ...form, parent_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="category-parent-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (top-level)</SelectItem>
                  {parents.filter((p) => p.id !== dialog?.id).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Image</Label>
              <ImageUpload value={form.image} onChange={(v) => setForm({ ...form, image: v })} label="category image" testId="category-image-upload" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cf-order">Sort order</Label>
              <Input id="cf-order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="category-order-input" />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="category-active-switch" />
            </div>
            <Button className="w-full rounded-full" onClick={save} disabled={saving} data-testid="save-category-btn">
              {saving ? "Saving…" : "Save category"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
